import type { Folder, Note } from "@hq/contracts";
import { buildFolderTree, type FolderTreeNode, sortNotes } from "@hq/core";
import { displayTitle, effectiveFolderId, sanitizeFileName, uniqueName } from "../lib";

/**
 * Maps our folders and notes onto the path-first vocabulary of `@pierre/trees`. Directory
 * paths end in `/`, note paths end in `.md` (`Hyper/Meetings/Standup.md`). Entities keep
 * their ids; `byPath` and `pathOf` translate in both directions.
 */

/** Notes are shown by title; the tree distinguishes files from folders by the trailing `/`. */
export const NOTE_EXTENSION: string = "";
const UNTITLED_FOLDER = "Untitled folder";

export type TreeEntryKind = "folder" | "note";
export type TreeEntry = { kind: TreeEntryKind; id: string };

export type TreePaths = {
  /** Every directory and file path in display order (folders before notes at each level). */
  paths: string[];
  /** Optional display-only root; it never becomes a stored folder. */
  rootPath: string;
  byPath: Map<string, TreeEntry>;
  /** Entity id → path. */
  pathOf: Map<string, string>;
};

/**
 * Directory paths for the live folder tree and file paths for the live notes inside them.
 * Names are sanitized (`/`, control characters) and made unique among siblings with ` (2)`,
 * folders follow `buildFolderTree` order (`sortOrder`, then name), notes follow their persisted Library order (legacy folders retain pinned-first, newest). Notes whose folder is missing or tombstoned sit at the root.
 */
export function buildTreePaths(
  folders: readonly Folder[],
  notes: readonly Note[],
  rootPath = "",
): TreePaths {
  const liveFolders = folders.filter((f) => f.deletedAt === undefined);
  const notesByFolder = new Map<string | null, Note[]>();
  for (const note of notes) {
    if (note.deletedAt !== undefined) continue;
    const key = effectiveFolderId(note, liveFolders);
    const list = notesByFolder.get(key) ?? [];
    list.push(note);
    notesByFolder.set(key, list);
  }

  const out: TreePaths = {
    rootPath,
    paths: rootPath ? [rootPath] : [],
    byPath: new Map(),
    pathOf: new Map(),
  };
  const put = (path: string, entry: TreeEntry) => {
    out.paths.push(path);
    out.byPath.set(path, entry);
    out.pathOf.set(entry.id, path);
  };

  const walk = (nodes: readonly FolderTreeNode[], parentId: string | null, prefix: string) => {
    const taken = new Set<string>();
    for (const node of nodes) {
      const name = uniqueName(taken, sanitizeFileName(node.folder.name, UNTITLED_FOLDER));
      const path = `${prefix}${name}/`;
      put(path, { kind: "folder", id: node.folder.id });
      walk(node.children, node.folder.id, path);
    }
    for (const note of sortNotes(notesByFolder.get(parentId) ?? [], "manual")) {
      const name = uniqueName(taken, sanitizeFileName(displayTitle(note)), NOTE_EXTENSION);
      put(`${prefix}${name}`, { kind: "note", id: note.id });
    }
  };
  walk(buildFolderTree(liveFolders), null, rootPath);
  return out;
}

/** The directory a path lives in, ending in `/`; `""` for the root. */
export function parentPathOf(path: string): string {
  const trimmed = path.endsWith("/") ? path.slice(0, -1) : path;
  const index = trimmed.lastIndexOf("/");
  return index < 0 ? "" : trimmed.slice(0, index + 1);
}

/**
 * The entity name a path segment stands for: the last segment, without the trailing `/` of
 * a directory and without a note's `.md` extension. `kind` defaults from the trailing slash,
 * which rename events omit, so callers that know the kind pass it.
 */
export function nameOfPath(path: string, kind?: TreeEntryKind): string {
  const isFolder = kind === undefined ? path.endsWith("/") : kind === "folder";
  const trimmed = path.endsWith("/") ? path.slice(0, -1) : path;
  const leaf = trimmed.slice(trimmed.lastIndexOf("/") + 1);
  if (isFolder) return leaf;
  return NOTE_EXTENSION && leaf.toLowerCase().endsWith(NOTE_EXTENSION)
    ? leaf.slice(0, -NOTE_EXTENSION.length)
    : leaf;
}

/** Looks a path up whether or not it carries its canonical trailing slash. */
export function entryAt(
  byPath: ReadonlyMap<string, TreeEntry>,
  path: string,
): TreeEntry | undefined {
  return byPath.get(path) ?? byPath.get(path.endsWith("/") ? path.slice(0, -1) : `${path}/`);
}

export type DropResolution = TreeEntry & {
  /** `null` is the root, i.e. no folder. */
  newParentId: string | null;
};

/**
 * What a drop means for our entities: the dragged folder or note and the folder it lands in.
 * `targetPath` is the target directory path, or `null`/`""` for the root. Undefined when the
 * dragged path is unknown, the target is not a known folder, or a folder is dropped on itself
 * or one of its own descendants.
 */
export function resolveDrop(
  byPath: ReadonlyMap<string, TreeEntry>,
  draggedPath: string,
  targetPath: string | null,
  rootPath = "",
): DropResolution | undefined {
  const dragged = entryAt(byPath, draggedPath);
  if (!dragged) return undefined;
  const dir = draggedPath.endsWith("/") ? draggedPath : `${draggedPath}/`;
  if (
    targetPath === null ||
    targetPath === "" ||
    targetPath.replace(/\/$/, "") === rootPath.replace(/\/$/, "")
  )
    return { ...dragged, newParentId: null };
  const targetDir = targetPath.endsWith("/") ? targetPath : `${targetPath}/`;
  const target = byPath.get(targetDir);
  if (!target || target.kind !== "folder") return undefined;
  if (dragged.kind === "folder" && targetDir.startsWith(dir)) return undefined;
  return { ...dragged, newParentId: target.id };
}

export type RenameEvent = { sourcePath: string; destinationPath: string; isFolder: boolean };
export type RenameResolution = TreeEntry & { newName: string };

/**
 * The entity a rename event addresses and the name it should take. Undefined when the source
 * is unknown or the new name is blank.
 */
export function resolveRename(
  byPath: ReadonlyMap<string, TreeEntry>,
  event: RenameEvent,
): RenameResolution | undefined {
  const entry = entryAt(byPath, event.sourcePath);
  if (!entry) return undefined;
  const newName = nameOfPath(event.destinationPath, entry.kind).trim();
  if (!newName) return undefined;
  return { ...entry, newName };
}

/** The visible sibling order, including orphan folders shown at the library root. */
export function folderSiblings(tree: TreePaths, id: string): string[] {
  const path = tree.pathOf.get(id);
  if (!path) return [];
  return tree.paths
    .filter((candidate) => parentPathOf(candidate) === parentPathOf(path))
    .map((candidate) => tree.byPath.get(candidate))
    .filter((entry) => entry?.kind === "folder")
    .map((entry) => entry!.id);
}

/** Insert one folder one slot earlier/later, repairing tied ranks only when necessary. */
export function folderReorderPlan(
  tree: TreePaths,
  folders: readonly Folder[],
  id: string,
  direction: -1 | 1,
): { id: string; sortOrder: number }[] {
  const siblings = folderSiblings(tree, id);
  const from = siblings.indexOf(id);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= siblings.length) return [];
  siblings.splice(from, 1);
  siblings.splice(to, 0, id);
  const ranks = new Map(folders.map((folder) => [folder.id, folder.sortOrder]));
  const previous = ranks.get(siblings[to - 1] ?? "");
  const next = ranks.get(siblings[to + 1] ?? "");
  const rank =
    previous === undefined
      ? (next ?? 0) - 1024
      : next === undefined
        ? previous + 1024
        : (previous + next) / 2;
  if (
    Number.isFinite(rank) &&
    (previous === undefined || rank > previous) &&
    (next === undefined || rank < next)
  ) {
    return [{ id, sortOrder: rank }];
  }
  return siblings.map((sibling, index) => ({ id: sibling, sortOrder: (index + 1) * 1024 }));
}

/** Only siblings of the same kind can be ordered; a folder's center remains a move target. */
export function siblingReorderPaths(
  tree: TreePaths,
  sources: readonly string[],
  target: string,
  edge: "before" | "after",
): TreeEntry[] {
  const destination = tree.byPath.get(target);
  if (!destination || sources.length === 0 || sources.includes(target)) return [];
  const parent = parentPathOf(target);
  if (
    sources.some(
      (path) => parentPathOf(path) !== parent || tree.byPath.get(path)?.kind !== destination.kind,
    )
  )
    return [];
  const siblings = tree.paths.filter(
    (path) => parentPathOf(path) === parent && tree.byPath.get(path)?.kind === destination.kind,
  );
  const picked = new Set(sources);
  const moving = siblings.filter((path) => picked.has(path));
  const rest = siblings.filter((path) => !picked.has(path));
  rest.splice(rest.indexOf(target) + (edge === "after" ? 1 : 0), 0, ...moving);
  if (rest.every((path, index) => path === siblings[index])) return [];
  return rest.map((path) => tree.byPath.get(path)!);
}
